import {
  MEDIA_PROTOCOL_VERSION,
  parseAvailableShare as parseProtocolAvailableShare,
  parseMediaDescriptor,
  type AvailableShare,
  type MediaDescriptor,
  type MediaRendition,
  type OwnerMediaControlMessage,
  type ShareGrant,
} from "@aasaan-share/protocol";

import type {
  ConnectionClientEvents,
  ViewerConnectionTransport,
} from "./viewer-transport.ts";
import type {
  MediaRequestInput,
  MediaResult,
  TransferUpdate,
  ViewerMediaTransport,
  ViewerTransferMeasurement,
} from "./viewer-media.ts";

type StorageShare = {
  available: AvailableShare;
  media: readonly MediaDescriptor[];
  mediaUrls: ReadonlyMap<string, string>;
};

type StorageBootstrap = {
  grants: readonly ShareGrant[];
  shares: readonly StorageShare[];
};

const BACKGROUND_REFRESH_INTERVAL_MS = 45_000;

export class StorageViewerConnectionClient implements ViewerConnectionTransport {
  media: ViewerMediaTransport;
  readonly #events: ConnectionClientEvents;
  readonly #shares = new Map<string, StorageShare>();
  #connectionToken: string | null = null;
  #grantSignature = "";
  #needsAdmissionUpdate = true;
  #refreshTimer: ReturnType<typeof setTimeout> | null = null;
  #stopped = true;

  constructor(events: ConnectionClientEvents) {
    this.#events = events;
    this.media = new StorageMediaReceiver(
      () => this.refresh(),
      events.onTransfer,
      events.onTransferMeasurement,
    );
  }

  connect(connectionToken: string): void {
    this.#connectionToken = connectionToken;
    this.#needsAdmissionUpdate = true;
    this.#stopped = false;
    void this.refresh(true);
  }

  retry(): void {
    if (!this.#connectionToken) return;
    void this.refresh(true);
  }

  close(): void {
    this.#stopped = true;
    this.#connectionToken = null;
    this.#grantSignature = "";
    this.#needsAdmissionUpdate = true;
    this.clearRefreshTimer();
    this.media.cancelAll("connection-closed");
    this.#shares.clear();
  }

  requestManifest(input: {
    readonly after: string | null;
    readonly limit?: number;
    readonly requestId: string;
    readonly revision: string | null;
    readonly shareId: string;
  }): boolean {
    const share = this.#shares.get(input.shareId);
    if (!share) return false;
    const offset = parseCursor(input.after, share.available.revision);
    if (offset === null) return false;
    const limit = Math.min(100, input.limit ?? 20);
    const media = share.media.slice(offset, offset + limit);
    const nextOffset = offset + media.length;
    queueMicrotask(() => {
      this.#events.onControl({
        media,
        nextCursor: nextOffset < share.media.length
          ? `storage-v1.${share.available.revision}.${nextOffset}`
          : null,
        requestId: input.requestId,
        revision: share.available.revision,
        shareId: input.shareId,
        totalMediaCount: share.media.length,
        type: "manifest.page",
        v: MEDIA_PROTOCOL_VERSION,
      });
    });
    return true;
  }

  private async refresh(announce = false): Promise<void> {
    const connectionToken = this.#connectionToken;
    if (!connectionToken || this.#stopped) return;
    if (announce) this.#events.onAdmission("connecting");
    try {
      const response = await fetch("/api/storage/shares", {
        headers: { Authorization: `Bearer ${connectionToken}` },
      });
      const value = await response.json().catch(() => null);
      if (!response.ok) throw new Error("Storage access is unavailable.");
      const bootstrap = parseBootstrap(value);
      if (connectionToken !== this.#connectionToken || this.#stopped) return;
      const grantSignature = JSON.stringify(bootstrap.grants);
      const changed = grantSignature !== this.#grantSignature || storageBootstrapChanged(
        this.#shares,
        bootstrap,
      );
      this.#shares.clear();
      for (const share of bootstrap.shares) {
        this.#shares.set(share.available.shareId, share);
      }
      this.#grantSignature = grantSignature;
      (this.media as StorageMediaReceiver).replaceUrls(bootstrap.shares);
      if (changed || announce || this.#needsAdmissionUpdate) {
        this.#events.onGrants(bootstrap.grants);
        this.#events.onShares(bootstrap.shares.map((share) => share.available));
        this.#events.onAdmission("connected");
        this.#needsAdmissionUpdate = false;
      }
    } catch {
      if (
        announce
        && connectionToken === this.#connectionToken
        && !this.#stopped
      ) {
        this.#needsAdmissionUpdate = true;
        this.#events.onAdmission("unavailable");
      }
    } finally {
      this.scheduleRefresh();
    }
  }

  private scheduleRefresh(): void {
    this.clearRefreshTimer();
    if (this.#stopped || !this.#connectionToken) return;
    this.#refreshTimer = setTimeout(() => {
      this.#refreshTimer = null;
      void this.refresh();
    }, BACKGROUND_REFRESH_INTERVAL_MS);
  }

  private clearRefreshTimer(): void {
    if (this.#refreshTimer === null) return;
    clearTimeout(this.#refreshTimer);
    this.#refreshTimer = null;
  }
}

function storageBootstrapChanged(
  current: ReadonlyMap<string, StorageShare>,
  next: StorageBootstrap,
): boolean {
  if (current.size !== next.shares.length) return true;
  for (const share of next.shares) {
    const previous = current.get(share.available.shareId);
    if (
      !previous
      || JSON.stringify(previous.available) !== JSON.stringify(share.available)
      || JSON.stringify(previous.media) !== JSON.stringify(share.media)
    ) {
      return true;
    }
  }
  return false;
}

class StorageMediaReceiver implements ViewerMediaTransport {
  readonly #pending = new Map<string, {
    readonly controller: AbortController;
    readonly kind: MediaDescriptor["kind"];
    readonly shareId: string;
  }>();
  readonly #urls = new Map<string, string>();

  constructor(
    private readonly refresh: () => Promise<void>,
    private readonly onTransfer: (update: TransferUpdate) => void,
    private readonly onMeasurement?: (
      measurement: ViewerTransferMeasurement,
    ) => void,
  ) {}

  replaceUrls(shares: readonly StorageShare[]): void {
    this.#urls.clear();
    for (const share of shares) {
      for (const [key, url] of share.mediaUrls) this.#urls.set(key, url);
    }
  }

  async request(input: MediaRequestInput): Promise<MediaResult> {
    if (this.#pending.has(input.requestId)) {
      throw new Error("Media request ID is already in use");
    }
    const rendition = renditionDescriptor(input.descriptor, input.rendition);
    if (!rendition) throw new Error("Requested rendition is unavailable");
    const controller = new AbortController();
    this.#pending.set(input.requestId, {
      controller,
      kind: input.descriptor.kind,
      shareId: input.shareId,
    });
    const requestedAt = Date.now();
    this.emit(input, "queued", 0);
    try {
      this.emit(input, "starting", 0);
      let response = await this.fetchMedia(input, controller.signal);
      if (response.status === 404 || response.status === 401) {
        await this.refresh();
        response = await this.fetchMedia(input, controller.signal);
      }
      if (!response.ok) throw new Error("Storage media is unavailable");
      const firstByteAt = Date.now();
      this.emit(input, "transferring", 0.5);
      const blob = await response.blob();
      if (blob.size !== rendition.byteLength) {
        throw new Error("Storage media length does not match its manifest");
      }
      this.emit(input, "verifying", 1);
      if (rendition.sha256 && await sha256(blob) !== rendition.sha256) {
        throw new Error("Storage media integrity check failed");
      }
      const completedAt = Date.now();
      this.onMeasurement?.({
        byteLength: blob.size,
        deliveryTransport: "storage-cdn",
        finalizeDurationMs: 0,
        firstByteLatencyMs: Math.max(0, firstByteAt - requestedAt),
        receiveDurationMs: Math.max(0, completedAt - firstByteAt),
        totalDurationMs: Math.max(0, completedAt - requestedAt),
        transferClass: transferClass(input.descriptor, input.rendition),
      });
      this.emit(input, "completed", 1);
      return {
        blob,
        byteLength: blob.size,
        mimeType: rendition.mimeType,
        requestId: input.requestId,
      };
    } catch (error) {
      this.emit(input, controller.signal.aborted ? "cancelled" : "failed", 0);
      throw error;
    } finally {
      this.#pending.delete(input.requestId);
    }
  }

  cancel(requestId: string): void {
    this.#pending.get(requestId)?.controller.abort();
  }

  cancelShare(shareId: string): void {
    for (const pending of this.#pending.values()) {
      if (pending.shareId === shareId) pending.controller.abort();
    }
  }

  cancelVideo(): void {
    for (const pending of this.#pending.values()) {
      if (pending.kind === "video") pending.controller.abort();
    }
  }

  cancelAll(): void {
    for (const pending of this.#pending.values()) pending.controller.abort();
  }

  private fetchMedia(
    input: MediaRequestInput,
    signal: AbortSignal,
  ): Promise<Response> {
    const url = this.#urls.get(storageUrlKey(
      input.shareId,
      input.descriptor.mediaId,
      input.rendition,
    ));
    if (!url) throw new Error("Storage media URL is unavailable");
    return fetch(url, { signal });
  }

  private emit(
    input: MediaRequestInput,
    state: TransferUpdate["state"],
    progress: number,
  ): void {
    this.onTransfer({
      progress,
      requestId: input.requestId,
      shareId: input.shareId,
      state,
    });
  }
}

function parseBootstrap(value: unknown): StorageBootstrap {
  if (!isRecord(value) || !Array.isArray(value.grants) || !Array.isArray(value.shares)) {
    throw new Error("Storage response is invalid");
  }
  const grants = value.grants.map(parseGrant);
  const shares = value.shares.map(parseShare);
  if (shares.length === 0) throw new Error("Storage response is empty");
  return { grants, shares };
}

function parseGrant(value: unknown): ShareGrant {
  if (!isRecord(value) || !isRecord(value.capabilities)) {
    throw new Error("Storage grant is invalid");
  }
  if (
    (value.access !== "view" && value.access !== "download")
    || typeof value.expiresAt !== "string"
    || typeof value.shareId !== "string"
    || typeof value.capabilities.videoDownloadV1 !== "boolean"
  ) {
    throw new Error("Storage grant is invalid");
  }
  return {
    access: value.access,
    canContribute: value.canContribute === true,
    capabilities: { videoDownloadV1: value.capabilities.videoDownloadV1 },
    expiresAt: value.expiresAt,
    shareId: value.shareId,
    ...(typeof value.viewerRecipientId === "string"
      ? { viewerRecipientId: value.viewerRecipientId }
      : {}),
  };
}

function parseShare(value: unknown): StorageShare {
  if (!isRecord(value) || !isRecord(value.share) || !Array.isArray(value.media)) {
    throw new Error("Storage share is invalid");
  }
  const available = parseAvailableShare(value.share);
  const media = value.media.map(parseDescriptor);
  if (!Array.isArray(value.mediaUrls)) throw new Error("Storage URLs are invalid");
  const mediaUrls = new Map(value.mediaUrls.map((entry) => {
    if (!isRecord(entry) || typeof entry.mediaId !== "string" || typeof entry.url !== "string") {
      throw new Error("Storage URL is invalid");
    }
    const rendition = entry.rendition;
    if (rendition !== "thumbnail" && rendition !== "preview" && rendition !== "original") {
      throw new Error("Storage URL is invalid");
    }
    return [storageUrlKey(available.shareId, entry.mediaId, rendition), entry.url];
  }));
  return { available, media, mediaUrls };
}

function parseAvailableShare(value: Record<string, unknown>): AvailableShare {
  try {
    return parseProtocolAvailableShare(value);
  } catch {
    throw new Error("Storage share is invalid");
  }
}

function parseDescriptor(value: unknown): MediaDescriptor {
  try {
    return parseMediaDescriptor(value);
  } catch {
    throw new Error("Storage media descriptor is invalid");
  }
}

function parseCursor(cursor: string | null, revision: string): number | null {
  if (cursor === null) return 0;
  const match = /^storage-v1\.([^.]+)\.(\d+)$/.exec(cursor);
  if (!match || match[1] !== revision) return null;
  const offset = Number(match[2]);
  return Number.isSafeInteger(offset) && offset >= 0 ? offset : null;
}

function renditionDescriptor(
  descriptor: MediaDescriptor,
  rendition: MediaRendition,
) {
  if (rendition === "thumbnail") return descriptor.renditions.thumbnail;
  if (rendition === "original") return descriptor.renditions.original;
  return descriptor.kind === "image" ? descriptor.renditions.preview : undefined;
}

function storageUrlKey(
  shareId: string,
  mediaId: string,
  rendition: MediaRendition,
): string {
  return `${shareId}:${mediaId}:${rendition}`;
}

async function sha256(blob: Blob): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", await blob.arrayBuffer());
  return Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");
}

function transferClass(
  descriptor: MediaDescriptor,
  rendition: MediaRendition,
): ViewerTransferMeasurement["transferClass"] {
  if (rendition === "thumbnail") return "thumbnail";
  if (rendition === "preview") return "preview";
  return descriptor.kind === "video" ? "video-original" : "image-original";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
